import React from "react";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import CustomImage from "../CustomImage";
import { ArrowRight, PaperclipIcon } from "lucide-react";
import { Message } from "../../types/message";
import { Option } from "../../types/option";
import { Attachment } from "../../types/attchment";

type MessageBubbleProps = {
  message: Message;
  isMobile: boolean;
  isFirstInGroup: boolean;
  isLastMessage: boolean;
  isLastInGroup: boolean;
  onOptionSelect?: (option: Option) => void;
  onSend: (message: Message) => void;
};

const MessageBubble: React.FC<MessageBubbleProps> = ({
  isMobile,
  message,
  isFirstInGroup,
  isLastMessage,
  isLastInGroup,
  onOptionSelect,
  onSend,
}) => {
  console.log("isFirstInGroup", isFirstInGroup);
  const isUser = message.sender_type === "user";
  const maxLength = isMobile ? 38 : 40;
  // @ts-ignore
  const multiLine = message?.content?.length > maxLength || message.content?.includes("\n");

  const borderRadiusClass = multiLine
    ? "rounded-lg p-4 whitespace-pre-wrap"
    : "rounded-full px-4 py-2";

  const sanitizedContent = message.content
    ? DOMPurify.sanitize(message.content)
    : "";

  const previewableTypes = ["image", "video", "audio"];
  const previewableAttachments =
    message.attachments?.filter((attachment) =>
      previewableTypes.includes(attachment?.type || "")
    ) || [];
  const nonPreviewableAttachments =
    message.attachments?.filter(
      (attachment) => !previewableTypes.includes(attachment?.type || "")
    ) || [];

  const renderPreview = (attachment: Attachment, idx: number) => {
    if (attachment.type === "image") {
      return (
        <img
          key={idx}
          src={attachment.url}
          alt={attachment.filename}
          className="w-full h-40 rounded-lg object-cover"
        />
      );
    } else if (attachment.type === "video") {
      return (
        <video
          key={idx}
          src={attachment.url}
          className="w-full h-40 rounded-lg object-cover"
        />
      );
    } else if (attachment.type === "audio") {
      return (
        <audio
          key={idx}
          controls
          src={attachment.url}
          className="max-w-xs rounded-lg mt-2"
        />
      );
    }
    return null;
  };

  const onEmailSubmit = (e: any, message: any) => {
    e?.preventDefault();
    console.log(message);
    const formData = new FormData(e.target);
    const formValues: any = {};
    formData.forEach((value: any, key) => {
      onSend({
        content: value,
        sender_type: "user",
        sender: null,
        createdAt: new Date(),
        attachments: [],
      });
    });
  };

  return (
    <>
      <div
        className={`flex ${isUser ? "justify-end" : "justify-start"} ${
          isLastInGroup ? "mb-6" : "mb-2"
        }`}
      >
        <div className="relative group flex flex-col">
          <div
            className={`flex items-start ${
              isUser ? "flex-row-reverse" : "flex-row"
            }`}
          >
            {!isUser && isLastInGroup && (
              <div className="flex-shrink-0 mr-2">
                <CustomImage
                  src={message.metadata?.avatar_url || ""}
                  alt={message.metadata?.first_name || ""}
                  size={38}
                />
              </div>
            )}

            {!isUser && !isLastInGroup && (
              <div className="flex-shrink-0 mr-2 w-10"></div>
            )}

            {sanitizedContent.length > 0 && (
              <div
                className={`max-w-xs text-sm border ${
                  isUser
                    ? "bg-secondary-500 text-white"
                    : !isUser
                    ? "bg-white text-black"
                    : "bg-white text-black"
                } ${borderRadiusClass} relative`}
              >
                <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
              </div>
            )}
            
          </div>
          {!isLastMessage && (
            <div
              className={`absolute top-0 bg-gray-500 rounded-md px-1.5 py-0.5 text-white -mt-2 text-xs truncate text-ellipsis ${
                isUser
                  ? `text-right left-0 self-end ${
                      sanitizedContent.length > 5 ? "-ml-4" : "-ml-10"
                    }`
                  : "text-left right-0 self-start"
              } transition-opacity duration-300 opacity-0 group-hover:opacity-80`}
            >
              {dayjs(message.createdAt).fromNow()}
            </div>
          )}

          {isLastMessage && (
            <div
              className={`text-xs mt-1 ml-12 ${
                isUser ? "text-right" : "text-left"
              }`}
            >
              {isUser ? "You" : message.metadata?.first_name} &#x2022;{" "}
              {dayjs(message.createdAt).fromNow()}
            </div>
          )}
          {(previewableAttachments.length > 0 ||
              nonPreviewableAttachments.length > 0) && (
              <div className="ml-12 mt-4">
                {previewableAttachments.length > 0 && (
                  <div className="flex flex-col">
                    {previewableAttachments.map((attachment, idx) => (
                      <div
                        key={idx}
                        className={`flex ${
                          isUser ? "justify-end" : "justify-start"
                        } mb-2`}
                      >
                        <div className="relative group flex flex-col">
                          <div
                            className={`flex flex-col items-start ${
                              isUser ? "flex-row-reverse" : "flex-row"
                            }`}
                          >
                            <div
                              className={`max-w-xs text-sm border border-dashed border-secondary-500 ${
                                isUser
                                  ? "bg-secondary-500 text-black"
                                  : !isUser
                                  ? "bg-white text-black"
                                  : "bg-white text-black"
                              } rounded-lg p-2 relative h-auto`}
                            >
                              {/* Render File Icon and Details */}
                              <div className="flex flex-col items-center w-full h-full space-y-2">
                                {renderPreview(attachment, idx)}
                                <div>
                                  <a
                                    href={attachment?.url || ""}
                                    target="_blank"
                                    className="text-sm font-medium italic text-ellipsis line-clamp-1"
                                  >
                                    {attachment.filename}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {nonPreviewableAttachments.length > 0 && (
                  <div className="flex flex-col">
                    {nonPreviewableAttachments.map((attachment, idx) => (
                      <div
                        key={idx}
                        className={`flex ${
                          isUser ? "justify-end" : "justify-start"
                        } mb-2`}
                      >
                        <div className="relative group flex flex-col">
                          <div
                            className={`flex items-start ${
                              isUser ? "flex-row-reverse" : "flex-row"
                            }`}
                          >
                            <div
                              className={`max-w-xs text-sm border border-dashed border-secondary-500 ${
                                isUser
                                  ? "bg-secondary-500 text-black"
                                  : !isUser
                                  ? "bg-white text-black"
                                  : "bg-white text-black"
                              } rounded-lg px-4 py-3 relative`}
                            >
                              {/* Render File Icon and Details */}
                              <div className="flex items-center">
                                {/* File Icon */}
                                <div className="mr-2">
                                  <div className="h-8 w-8 rounded-full bg-secondary-500 p-2">
                                    <PaperclipIcon className="size-4 text-white" />
                                  </div>
                                </div>
                                {/* File Details */}
                                <div>
                                  <a
                                    href={attachment?.url || ""}
                                    target="_blank"
                                  >
                                    {attachment.filename}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
        </div>
      </div>

      {(message.sender_type === "bot" || message.sender_type === "agent") &&
        message.options &&
        message.options.length > 0 &&
        onOptionSelect && (
          <div className="flex justify-end mb-4 px-4">
            <div className="flex justify-end flex-wrap gap-2 w-4/5">
              {message.options.map((option: any, idx) =>
                option.type === "url" ? (
                  <button
                    key={idx}
                    onClick={() => onOptionSelect(option)}
                    className={`border border-primary-600 px-3 py-2 rounded-lg text-sm focus:outline-none transition-colors duration-200 bg-white text-primary-600 hover:bg-primary-600 hover:text-white`}
                  >
                    {option.label}
                  </button>
                ) : (
                  <button
                    key={idx}
                    onClick={() => onOptionSelect(option)}
                    className={`border border-primary-600 px-3 py-2 rounded-lg text-sm focus:outline-none transition-colors duration-200 bg-white text-primary-600 hover:bg-primary-600 hover:text-white`}
                  >
                    {option.label}
                  </button>
                )
              )}
            </div>
          </div>
        )}

      {(message.sender_type === "bot" || message.sender_type === "agent") &&
        message.input && (
          <div className="flex justify-end mb-4 px-4">
            <div className="flex justify-end flex-wrap gap-2 w-4/5">
              <form
                onSubmit={(e: any) => onEmailSubmit(e, message?.input)}
                className="w-full flex items-center space-x-2 bg-white shadow-sm border rounded-lg p-2"
              >
                <input
                  type={message.input?.type}
                  className={`flex-1 p-3 bg-gray-50 peer transition-[outline] placeholder-[#a3a3a3] text-[--title-text-color] shadow-sm shadow-gray-950/5 outline-2 focus:outline-secondary-500 focus:outline -outline-offset-1 border dark:shadow-gray-950/35 data-[invalid]:border-danger-600 focus:data-[invalid]:outline-danger-600 dark:data-[invalid]:border-danger-500 dark:focus:data-[invalid]:outline-danger-500 data-[valid]:border-success-600 focus:data-[valid]:outline-success-600 dark:data-[valid]:border-success-500 dark:focus:data-[valid]:outline-success-500 text-sm h-9 rounded-lg dark:bg-[--ui-bg] ${!isLastMessage && "pointer-events-none"}`}
                  placeholder={message.input?.placeholder}
                  disabled={!isLastMessage}
                  name={message.input.label}
                />
                <button className="size-8 rounded-full bg-secondary-500 p-2 flex justify-center items-center text-white">
                  <ArrowRight />
                </button>
              </form>
            </div>
          </div>
        )}
    </>
  );
};

export default MessageBubble;
